import { useState } from 'react';
import { cn } from '@/lib/utils';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import ToggleButtons from '@/components/buttons/ToggleButtons.tsx';
import PrimitiveCheckbox from '@/components/form/_primitives/PrimitiveCheckbox.tsx';
import PrimitiveInput from '@/components/form/_primitives/PrimitiveInput.tsx';
import PrimitiveTextarea from '@/components/form/_primitives/PrimitiveTextarea.tsx';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputHint from '@/components/form/base/InputHint.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import LoadError from '@/components/ui/LoadError.tsx';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { PersonTileVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { ParticipantStatus } from '@/features/dashboard/participants/types/ParticipantStatus.ts';
import { getParticipantStatusLabel } from '@/features/dashboard/participants/types/ParticipantStatus.utils.ts';
import {
  RefundChannel,
  WithdrawalFeesDto,
} from '@/features/dashboard/bookingChanges/types/dto/WithdrawalDto.ts';
import useWithdrawalPreview from '@/features/dashboard/bookingChanges/hooks/useWithdrawalPreview.ts';
import useExecuteWithdrawal from '@/features/dashboard/bookingChanges/hooks/useExecuteWithdrawal.ts';
import { isZeroMoney } from '@/features/dashboard/bookingChanges/utils/money.ts';
import WithdrawalSettlementPanel from './WithdrawalSettlementPanel.tsx';
import {
  FEE_HINT,
  JUSTIFICATION_HINT,
  REFUND_CHANNEL_LABELS,
  WITHDRAWAL_INTRO,
  WITHDRAWAL_NO_SELECTION,
  WITHDRAWAL_TITLE,
} from './withdrawalCopy.ts';

const REFUND_CHANNEL_OPTIONS = Object.entries(REFUND_CHANNEL_LABELS).map(
  ([value, label]) => ({ value, label }),
);

interface WithdrawalModalProps {
  bookingId: string;
  participants: PersonTileVm[];
  /** Osoba, od której operator zaczął — zaznaczona z góry. */
  initialParticipantId?: string;
  onClose: () => void;
}

/**
 * Rezygnacja uczestnika.
 *
 * Rozliczenia nie liczy tu nic: każdy wybór osoby i każda zmiana potrącenia
 * odpytuje `…/withdrawal/preview`, czyli ten sam kalkulator, który wykona
 * wprowadzenie. Dzięki temu kwota z ekranu jest kwotą, która się zadzieje.
 */
const WithdrawalModal = ({
  bookingId,
  participants,
  initialParticipantId,
  onClose,
}: WithdrawalModalProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(
    initialParticipantId ? [initialParticipantId] : [],
  );
  const [fees, setFees] = useState<WithdrawalFeesDto>({});
  const [channels, setChannels] = useState<
    Partial<Record<Currency, RefundChannel>>
  >({});
  const [justification, setJustification] = useState('');

  const preview = useWithdrawalPreview(bookingId, selectedIds, fees);
  const execute = useExecuteWithdrawal(bookingId);

  const toggleParticipant = (participantId: string) =>
    setSelectedIds((current) =>
      current.includes(participantId)
        ? current.filter((id) => id !== participantId)
        : [...current, participantId],
    );

  const setFee = (currency: Currency, value: string) =>
    setFees((current) => {
      const amount = Number.parseFloat(value);
      // Puste pole znaczy „bez potrącenia”, a nie zero — waluta wypada z mapy.
      if (!value.trim() || Number.isNaN(amount)) {
        const { [currency]: _dropped, ...rest } = current;
        return rest;
      }
      return { ...current, [currency]: amount };
    });

  const settlement = preview.data;
  const refundCurrencies = (settlement?.lines ?? [])
    .filter((line) => !isZeroMoney(line.refund))
    .map((line) => line.share.currency);

  const canSubmit =
    selectedIds.length > 0 &&
    justification.trim().length > 0 &&
    !!settlement &&
    !execute.isPending;

  const submit = () =>
    execute.mutate(
      {
        participantIds: selectedIds,
        fees: Object.keys(fees).length > 0 ? fees : null,
        refundChannels: Object.keys(channels).length > 0 ? channels : null,
        justification: justification.trim(),
      },
      { onSuccess: onClose },
    );

  return (
    <Modal onClose={onClose} className="max-w-[560px]">
      <ModalHeader
        title={WITHDRAWAL_TITLE}
        description={WITHDRAWAL_INTRO}
        onClose={onClose}
      />

      <ModalContent className="flex flex-col gap-5">
        <div>
          <div className={columnLabelClass}>Kto rezygnuje</div>
          <div className="mt-2 flex flex-col">
            {/* Uczestnik, który już odszedł, zostaje na liście — inaczej operator
                nie wiedziałby, czemu rezerwacja ma mniej osób — ale nie da się
                go zaznaczyć drugi raz. */}
            {participants.map((participant) => (
              <label
                key={participant.person.id}
                className={cn(
                  'flex items-start gap-2.5 border-b border-neutral-100 py-2.5 last:border-b-0',
                  participant.withdrawn
                    ? 'cursor-not-allowed opacity-60'
                    : 'cursor-pointer',
                )}
              >
                <PrimitiveCheckbox
                  checked={selectedIds.includes(participant.person.id)}
                  disabled={participant.withdrawn}
                  onChange={() => toggleParticipant(participant.person.id)}
                />
                <span className="min-w-0">
                  <span className="block text-[15px] font-semibold text-ink">
                    {participant.person.fullName}
                  </span>
                  <span className="block text-[13px] text-neutral-500">
                    {participant.withdrawn
                      ? `${participant.person.roleLabel} · ${getParticipantStatusLabel(ParticipantStatus.WITHDRAWN).toLocaleLowerCase('pl')}`
                      : participant.person.roleLabel}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {selectedIds.length === 0 && (
          <p className="text-sm text-neutral-500">{WITHDRAWAL_NO_SELECTION}</p>
        )}

        {preview.isError && <LoadError subject="rozliczenia rezygnacji" />}

        {settlement && (
          <>
            <div className="flex flex-col gap-2">
              <InputLabel label="Potrącenie za odstąpienie" />
              {settlement.lines.map((line) => (
                <div
                  key={line.share.currency}
                  className="flex items-center gap-2"
                >
                  <PrimitiveInput
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0,00"
                    value={fees[line.share.currency] ?? ''}
                    onChange={(event) =>
                      setFee(line.share.currency, event.target.value)
                    }
                  />
                  <span className="text-sm font-semibold text-neutral-500">
                    {line.share.currency}
                  </span>
                </div>
              ))}
              <InputHint hint={FEE_HINT} />
            </div>

            <WithdrawalSettlementPanel settlement={settlement} />

            {refundCurrencies.map((currency) => (
              <ToggleButtons
                key={currency}
                label={`Zwrot w ${currency} przez`}
                options={REFUND_CHANNEL_OPTIONS}
                selected={channels[currency] ?? RefundChannel.TRANSFER}
                onChange={(value) =>
                  setChannels((current) => ({
                    ...current,
                    [currency]: value as RefundChannel,
                  }))
                }
              />
            ))}
          </>
        )}

        <div className="flex flex-col gap-1.5">
          <InputLabel label="Uzasadnienie" required />
          <PrimitiveTextarea
            rows={3}
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
          />
          <InputHint hint={JUSTIFICATION_HINT} />
        </div>
      </ModalContent>

      <ModalFooter>
        <ActionButton tone="outline" size="sm" onClick={onClose}>
          Anuluj
        </ActionButton>
        <ActionButton
          tone="danger"
          size="sm"
          disabled={!canSubmit}
          isLoading={execute.isPending}
          onClick={submit}
        >
          Wprowadź rezygnację
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default WithdrawalModal;
